/**
 * @author      luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright   Copyright (c) 2023, Zumper
 * @description SpecialtyHousingContext
 */
import { useDisclosure } from '@chakra-ui/react'
import { selectEntity } from '@zcode-ui/web-zumper-global-state'
import type { Listable, Property } from '@zcode/state-type'
import React, { createContext, useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import type { Values } from '../helpers'

type IntentType = 'Apply' | 'Message' | 'Tour' | 'Waitlist' | undefined

const defaultValues = {
  formValues: null,
  goToNextSpecialtyHousingModal: () => {},
  heading: '',
  intent: undefined,
  isSpecialtyHousing: false,
  isWaitlistForm: false,
  isSpecialtyHousingOpen: false,
  message: '',
  onOpenSpecialtyHousing: () => {},
  onCloseSpecialtyHousing: () => {},
  setFormValues: () => {},
  setIsWaitlistForm: () => {},
}

export interface MessageFormContextType<V extends Omit<Values, 'selectedDateTime'>> {
  formValues: V | null
  goToNextSpecialtyHousingModal: (submit: () => void) => void
  heading: string
  intent: IntentType
  isSpecialtyHousing: boolean
  isWaitlistForm?: boolean
  isSpecialtyHousingOpen: boolean
  message: string
  onOpenSpecialtyHousing: () => void
  onCloseSpecialtyHousing: () => void
  setFormValues: React.Dispatch<React.SetStateAction<V | null>>
  setIsWaitlistForm: React.Dispatch<React.SetStateAction<boolean>>
}

const MessageFormContext = createContext<MessageFormContextType<any>>(defaultValues)

const getHeaderAndMessage = (entity: Listable | Property | undefined | null) => {
  if (!entity) return []
  const {
    isAgeRestricted,
    isIncomeRestricted,
    isMilitaryHousing,
    isStudentHousing,
    incomeRestrictions,
  } = entity
  const restrictions = []
  if (isIncomeRestricted) {
    restrictions.push({
      heading: 'Income Restrictions',
      message:
        incomeRestrictions &&
        'restrictions' in incomeRestrictions &&
        incomeRestrictions.restrictions
          ? 'To qualify for this property, your annual income must be less than or equal to the amount in the table, depending on the number of people that will be living here. Contact the property for more information.'
          : 'To qualify for this property, your annual income must be within a certain range decided by the property. Please contact the property for more information.',
    })
  }
  if (isAgeRestricted) {
    restrictions.push({
      heading: 'Senior Living',
      message: 'This property has an age requirement.',
    })
  }
  if (isMilitaryHousing) {
    restrictions.push({
      heading: 'Military housing',
      message:
        'This property is intended for military personnel and may require special clearance to access.',
    })
  }
  if (isStudentHousing) {
    restrictions.push({
      heading: 'Student Housing',
      message: 'This property is intended for students only.',
    })
  }
  return restrictions
}

export const MessageFormContextProvider = <V extends Omit<Values, 'selectedDateTime'>>({
  children,
  intent,
}: {
  children:
    | React.ReactNode
    | ((props: {
        heading: string
        isSpecialtyHousingOpen: boolean
        isWaitlistForm: boolean
        onOpenSpecialtyHousing: () => void
        onCloseSpecialtyHousing: () => void
      }) => React.ReactNode)
  intent?: IntentType
}) => {
  const [formValues, setFormValues] = useState<V | null>(null)
  const [isWaitlistForm, setIsWaitlistForm] = useState(false)
  const [currentModalIndex, setCurrentModalIndex] = useState(0)
  const entity = useSelector(selectEntity)
  const {
    isOpen: isSpecialtyHousingOpen,
    onOpen: onOpenSpecialtyHousing,
    onClose: onCloseSpecialtyHousing,
  } = useDisclosure()
  const headersAndMessages = getHeaderAndMessage(entity)
  const { heading, message } = headersAndMessages[currentModalIndex] ?? { heading: '', message: '' }
  // if multiple specialty housing badges go to next modal on submit
  // if not resume submit
  const goToNextSpecialtyHousingModal = (submit: () => void) => {
    if (currentModalIndex < headersAndMessages.length - 1) {
      setCurrentModalIndex((prevIndex) => prevIndex + 1)
    } else {
      submit()
    }
  }

  return (
    <MessageFormContext.Provider
      value={{
        formValues,
        goToNextSpecialtyHousingModal,
        heading,
        intent,
        isSpecialtyHousingOpen,
        isSpecialtyHousing: Boolean(entity?.isSpecialtyHousing),
        isWaitlistForm,
        message,
        onOpenSpecialtyHousing,
        onCloseSpecialtyHousing,
        setFormValues,
        setIsWaitlistForm,
      }}
    >
      {typeof children === 'function'
        ? children({
            heading,
            isSpecialtyHousingOpen,
            isWaitlistForm,
            onOpenSpecialtyHousing,
            onCloseSpecialtyHousing,
          })
        : children}
    </MessageFormContext.Provider>
  )
}

export const useMessageFormContext = <V extends Omit<Values, 'selectedDateTime'>>() =>
  useContext<MessageFormContextType<V>>(MessageFormContext)
